/**
 * Shared commercial-certificate content and styling.
 *
 * One source of truth for the certificate face used in two places:
 *   - the on-page preview inside /licenses
 *   - the standalone HTML document downloaded / printed from /licenses
 *
 * The certificate keeps its own fixed gold-on-near-black palette and serif face only where the
 * document treatment calls for it. The wider layout intentionally follows the sales-certificate
 * look: dark hero, warm paper body, white inset cards, and a public verification footer.
 */
import { LICENSE_RIGHTS } from '../data/license';
import { escapeHtml } from './escapeHtml';
import { brandMarkSvg } from './printBrand';

export type CommercialCertificateStatus =
  | 'active'
  | 'revoked'
  | 'suspended'
  | 'refunded'
  | 'expired';

export interface CommercialCertificateFields {
  licenseNumber: string;
  customerName: string;
  email: string;
  purchaseDate: string;
  plan: string;
  /** Public verification URL for this certificate. */
  verifyUrl?: string;
  /** Self-contained data URL for the verification QR image. */
  qrSrc: string;
  /** Self-contained logo for exported documents; the live preview can use its app asset. */
  logoSrc?: string;
  /** Saved account profile picture. Absent keeps the certificate's profile icon. */
  avatarSrc?: string;
  downloadCount: number;
  version: string;
  status?: CommercialCertificateStatus;
}

interface CertificateTile {
  title: string;
  body: string;
}

const CERTIFICATE_NOTICE =
  'This certificate summarizes the rights granted under the Lyrixsa AI Commercial License Agreement. It is proof of purchase of a Commercial Creator License and is not itself the legal agreement, which is available at /legal/commercial-license.';

const STREAMING_RIGHTS = new Set([
  'Upload to Spotify',
  'Upload to Apple Music',
  'Upload to Amazon Music',
  'Upload to YouTube Music',
  'Upload to Deezer',
]);

const SOCIAL_RIGHTS = new Set(['Upload to TikTok', 'Upload to Instagram']);

function availableRightTitles(): Set<string> {
  return new Set(LICENSE_RIGHTS.map((right) => right.title));
}

function hasAny(titles: Set<string>, candidates: ReadonlySet<string>): boolean {
  for (const title of candidates) if (titles.has(title)) return true;
  return false;
}

function outlinedIconSvg(
  kind: 'user' | 'calendar' | 'shield',
  size = 24,
  stroke = '#E7B545'
): string {
  const common = `width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"`;

  switch (kind) {
    case 'calendar':
      return `<svg ${common}><rect x="3.5" y="5" width="17" height="15.5" rx="2.5" stroke="${stroke}" stroke-width="1.8"/><path d="M8 3.8V7" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/><path d="M16 3.8V7" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/><path d="M3.5 9.2H20.5" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/></svg>`;
    case 'shield':
      return `<svg ${common}><path d="M12 3.5 19 6.5V11.4c0 4.3-2.4 7.3-7 9.1-4.6-1.8-7-4.8-7-9.1V6.5l7-3Z" stroke="${stroke}" stroke-width="1.8" stroke-linejoin="round"/><path d="m9.1 12.4 1.9 1.9 4-4.3" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    case 'user':
    default:
      return `<svg ${common}><circle cx="12" cy="8" r="3.5" stroke="${stroke}" stroke-width="1.8"/><path d="M5 18.1c0-2.9 2.8-5.1 7-5.1s7 2.2 7 5.1c0 1.3-1 2.4-2.3 2.4H7.3C6 20.5 5 19.4 5 18.1Z" stroke="${stroke}" stroke-width="1.8" stroke-linejoin="round"/></svg>`;
  }
}

/**
 * A compact certificate-specific projection of the fuller rights fixture.
 *
 * The dashboard grid below the certificate shows the detailed per-platform list; the
 * certificate itself groups those rights into the more document-like blocks it needs while
 * still deriving them from the same source data instead of carrying an unrelated list.
 */
export function commercialCertificateTiles(): CertificateTile[] {
  const titles = availableRightTitles();
  const tiles: CertificateTile[] = [];

  if (titles.has('Sell music commercially')) {
    tiles.push({
      title: 'Sell & distribute',
      body: 'Sell, distribute, and profit from the music you create with Lyrixsa AI.',
    });
  }
  if (hasAny(titles, STREAMING_RIGHTS)) {
    tiles.push({
      title: 'Music platforms',
      body: 'Upload your music to streaming platforms covered by your Commercial Creator License.',
    });
  }
  if (titles.has('Monetize videos')) {
    tiles.push({
      title: 'YouTube & videos',
      body: 'Use your music in monetized videos and commercial video content.',
    });
  }
  if (titles.has('Client projects')) {
    tiles.push({
      title: 'Client projects',
      body: 'Deliver tracks as part of paid client work and final project handoff.',
    });
  }
  if (titles.has('Sell music commercially')) {
    tiles.push({
      title: 'Monetization',
      body: 'Monetize your music on commercial channels without additional usage fees.',
    });
  }
  if (hasAny(titles, SOCIAL_RIGHTS)) {
    tiles.push({
      title: 'Social media',
      body: 'Use your music in social content, shorts, reels, and platform-native posts.',
    });
  }
  if (titles.has('Sell music commercially')) {
    tiles.push({
      title: 'No copyright claims',
      body: 'This certificate documents your purchase and usage rights for Lyrixsa AI output.',
    });
  }
  if (titles.has('Sell music commercially')) {
    tiles.push({
      title: 'Unlimited use',
      body: 'Use covered Lyrixsa AI generations across personal and commercial projects.',
    });
  }
  if (titles.has('Worldwide usage')) {
    tiles.push({
      title: 'Global rights',
      body: 'These commercial rights apply worldwide, with no territory restriction stated here.',
    });
  }
  if (titles.has('Sell music commercially')) {
    tiles.push({
      title: 'Royalty-free',
      body: 'The certificate represents royalty-free commercial usage under the agreement terms.',
    });
  }

  return tiles;
}

export function commercialCertificateStatusLabel(
  status: CommercialCertificateStatus = 'active'
): string {
  switch (status) {
    case 'revoked':
      return 'Revoked';
    case 'suspended':
      return 'Suspended';
    case 'refunded':
      return 'Refunded';
    case 'expired':
      return 'Expired';
    case 'active':
    default:
      return 'Active';
  }
}

function commercialCertificateRows(
  fields: CommercialCertificateFields
): Array<{ label: string; value: string }> {
  return [
    { label: 'Licensee', value: fields.customerName },
    { label: 'Email', value: fields.email },
    { label: 'License type', value: fields.plan },
    { label: 'License ID', value: fields.licenseNumber },
    { label: 'Date issued', value: fields.purchaseDate },
    { label: 'Valid until', value: 'Lifetime' },
  ];
}

function certificateNotes(): string[] {
  return [
    'You own the rights to the music you create with Lyrixsa AI under this commercial licence.',
    'This certificate grants commercial usage rights worldwide, subject to the licence agreement.',
    'The agreement remains the governing legal document for these rights.',
    'Use the QR code or verification URL below to confirm authenticity publicly.',
  ];
}

function rightsIconSvg(title: string, stroke = '#E7B545'): string {
  const common = `width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"`;

  if (title.startsWith('Sell')) {
    return `<svg ${common}><path d="M4 7.5h2.2l1.4 7.1h8.3l2.1-5.1H8.2" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><circle cx="10" cy="18.2" r="1.2" fill="${stroke}"/><circle cx="16.2" cy="18.2" r="1.2" fill="${stroke}"/></svg>`;
  }
  if (title.includes('platform')) {
    return `<svg ${common}><circle cx="12" cy="12" r="8.5" stroke="${stroke}" stroke-width="1.8"/><path d="M9 13.6a4.4 4.4 0 0 1 6 0" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/><path d="M7.3 10.8a7 7 0 0 1 9.4 0" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/><circle cx="12" cy="16.3" r="1.1" fill="${stroke}"/></svg>`;
  }
  if (title.includes('video')) {
    return `<svg ${common}><rect x="4" y="6" width="16" height="12" rx="2.6" stroke="${stroke}" stroke-width="1.8"/><path d="m10 9.4 5 2.6-5 2.6V9.4Z" fill="${stroke}"/></svg>`;
  }
  if (title.includes('Client')) {
    return `<svg ${common}><rect x="5" y="8" width="14" height="10" rx="2" stroke="${stroke}" stroke-width="1.8"/><path d="M9 8V6.6A1.6 1.6 0 0 1 10.6 5h2.8A1.6 1.6 0 0 1 15 6.6V8" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/><path d="M5 12h14" stroke="${stroke}" stroke-width="1.8"/></svg>`;
  }
  if (title.includes('Monetization')) {
    return `<svg ${common}><circle cx="12" cy="12" r="8.5" stroke="${stroke}" stroke-width="1.8"/><path d="M12 7.3v9.4" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/><path d="M14.7 9.2c0-1-1.1-1.8-2.7-1.8s-2.7.8-2.7 1.8 1.1 1.8 2.7 1.8 2.7.8 2.7 1.8-1.1 1.8-2.7 1.8-2.7-.8-2.7-1.8" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  if (title.includes('Social')) {
    return `<svg ${common}><path d="M8.8 10.4A3.3 3.3 0 1 0 8.8 3.8a3.3 3.3 0 0 0 0 6.6ZM15.8 12.4a2.7 2.7 0 1 0 0-5.4 2.7 2.7 0 0 0 0 5.4ZM6 20.2v-.8c0-2.3 2.2-4.1 5-4.1s5 1.8 5 4.1v.8" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M17.7 20.2v-.5c0-1.5-.9-2.8-2.3-3.5" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/></svg>`;
  }
  if (title.includes('copyright')) {
    return `<svg ${common}><circle cx="12" cy="12" r="8.5" stroke="${stroke}" stroke-width="1.8"/><path d="M14.8 9.5a3.3 3.3 0 1 0 0 5" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round"/></svg>`;
  }
  if (title.includes('Unlimited')) {
    return `<svg ${common}><path d="M5 13.1c1.2-2.1 2.5-3.2 4-3.2 2.7 0 3.3 4.2 6 4.2 1.5 0 2.8-1.1 4-3.2" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 10.9c1.2 2.1 2.5 3.2 4 3.2 2.7 0 3.3-4.2 6-4.2 1.5 0 2.8 1.1 4 3.2" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
  if (title.includes('Global')) {
    return `<svg ${common}><circle cx="12" cy="12" r="8.5" stroke="${stroke}" stroke-width="1.8"/><path d="M3.8 12h16.4" stroke="${stroke}" stroke-width="1.8"/><path d="M12 3.5c2 2.1 3.2 5.2 3.2 8.5s-1.2 6.4-3.2 8.5c-2-2.1-3.2-5.2-3.2-8.5S10 5.6 12 3.5Z" stroke="${stroke}" stroke-width="1.8" stroke-linejoin="round"/></svg>`;
  }
  if (title.includes('Royalty')) {
    return `<svg ${common}><path d="M12 3.8 18.3 6.3v5c0 3.9-2.1 6.6-6.3 8.9-4.2-2.3-6.3-5-6.3-8.9v-5L12 3.8Z" stroke="${stroke}" stroke-width="1.8" stroke-linejoin="round"/><path d="m9 12.2 2 2 4-4.2" stroke="${stroke}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  return `<svg ${common}><circle cx="12" cy="12" r="1.8" fill="${stroke}"/></svg>`;
}

function verifyUrlText(fields: CommercialCertificateFields): string {
  const verifyUrl = fields.verifyUrl?.trim();
  if (verifyUrl) return verifyUrl.replace(/^https?:\/\//, '');
  return `lyrixsa.ai/license/verify/${fields.licenseNumber}`;
}

function detailBlock(label: string, value: string, icon: 'calendar' | 'user'): string {
  return `
    <div class="lyx-cert-detail">
      <span class="lyx-cert-detail-icon">${outlinedIconSvg(icon, 24)}</span>
      <span class="lyx-cert-detail-copy">
        <span class="lyx-cert-label">${escapeHtml(label)}</span>
        <span class="lyx-cert-value">${escapeHtml(value)}</span>
      </span>
    </div>
  `;
}

export function commercialCertificateCss(): string {
  return `
    .lyx-cert-root {
      --lyx-cert-scale: 1;
      box-sizing: border-box;
      width: 100%;
      color: #101114;
      background: #F4F0E8;
      border: 1px solid #E6D6AF;
      border-radius: calc(12px * var(--lyx-cert-scale));
      overflow: hidden;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
    }
    .lyx-cert-root *,
    .lyx-cert-root *::before,
    .lyx-cert-root *::after { box-sizing: border-box; }
    .lyx-cert-hero {
      position: relative;
      overflow: hidden;
      padding: calc(30px * var(--lyx-cert-scale)) calc(30px * var(--lyx-cert-scale)) calc(34px * var(--lyx-cert-scale));
      background:
        radial-gradient(circle at 84% 18%, rgba(231, 181, 69, 0.18), transparent 18%),
        radial-gradient(circle at 72% 56%, rgba(201, 162, 75, 0.11), transparent 26%),
        linear-gradient(135deg, #06070A 0%, #0F1014 54%, #171920 100%);
      color: #F5F1E8;
      border-bottom: calc(4px * var(--lyx-cert-scale)) solid #C9A24B;
    }
    .lyx-cert-hero::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(90% 38% at 76% 70%, transparent 42%, rgba(201, 162, 75, 0.11) 43%, transparent 45%, transparent 48%, rgba(201, 162, 75, 0.09) 49%, transparent 51%),
        radial-gradient(88% 30% at 12% 12%, transparent 56%, rgba(201, 162, 75, 0.08) 57%, transparent 59%);
      pointer-events: none;
    }
    .lyx-cert-hero-row {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: calc(20px * var(--lyx-cert-scale));
      margin-bottom: calc(28px * var(--lyx-cert-scale));
    }
    .lyx-cert-waveform {
      position: absolute;
      top: calc(68px * var(--lyx-cert-scale));
      right: calc(226px * var(--lyx-cert-scale));
      width: calc(350px * var(--lyx-cert-scale));
      height: calc(104px * var(--lyx-cert-scale));
      opacity: 0.26;
      background:
        linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.08) 20%, transparent 23%, transparent 77%, rgba(255,255,255,0.08) 80%, transparent 100%),
        repeating-linear-gradient(90deg, transparent 0 calc(11px * var(--lyx-cert-scale)), rgba(255,255,255,0.16) calc(11px * var(--lyx-cert-scale)) calc(13px * var(--lyx-cert-scale)), transparent calc(13px * var(--lyx-cert-scale)) calc(22px * var(--lyx-cert-scale)));
      clip-path: polygon(0 60%, 4% 60%, 7% 50%, 10% 60%, 14% 60%, 17% 38%, 20% 60%, 24% 60%, 28% 26%, 32% 60%, 36% 60%, 40% 42%, 44% 60%, 48% 60%, 52% 20%, 56% 60%, 60% 60%, 64% 45%, 68% 60%, 72% 60%, 76% 32%, 80% 60%, 84% 60%, 88% 47%, 92% 60%, 96% 60%, 100% 60%, 100% 100%, 0 100%);
      pointer-events: none;
    }
    .lyx-cert-brand {
      display: flex;
      align-items: center;
      gap: calc(12px * var(--lyx-cert-scale));
      min-width: 0;
    }
    .lyx-cert-brand-mark {
      display: grid;
      place-items: center;
      width: calc(54px * var(--lyx-cert-scale));
      height: calc(54px * var(--lyx-cert-scale));
      border-radius: 999px;
      border: 1px solid rgba(214, 163, 41, 0.52);
      background: rgba(9, 10, 13, 0.86);
      box-shadow: inset 0 0 0 1px rgba(214, 163, 41, 0.08);
      flex-shrink: 0;
    }
    .lyx-cert-brand-mark svg {
      display: block;
      width: calc(28px * var(--lyx-cert-scale));
      height: calc(28px * var(--lyx-cert-scale));
    }
    .lyx-cert-wordmark-block {
      min-width: 0;
    }
    .lyx-cert-wordmark {
      margin: 0 0 calc(4px * var(--lyx-cert-scale));
      color: #F5F1E8;
      font-family: 'Space Grotesk', 'Inter', sans-serif;
      font-size: calc(24px * var(--lyx-cert-scale));
      font-weight: 700;
      letter-spacing: 0.02em;
      line-height: 1;
      text-transform: uppercase;
    }
    .lyx-cert-wordmark-accent { color: #E6AC33; }
    .lyx-cert-brand-subtitle {
      margin: 0;
      color: #D3D5DA;
      font-size: calc(10.5px * var(--lyx-cert-scale));
      letter-spacing: 0.08em;
      text-transform: uppercase;
      line-height: 1.2;
    }
    .lyx-cert-seal {
      position: relative;
      z-index: 1;
      flex-shrink: 0;
      width: calc(174px * var(--lyx-cert-scale));
      text-align: center;
    }
    .lyx-cert-seal-disc {
      position: relative;
      border-radius: 999px;
      border: calc(8px * var(--lyx-cert-scale)) solid #C28F1B;
      box-shadow:
        inset 0 0 0 calc(2px * var(--lyx-cert-scale)) #E0B453,
        0 calc(8px * var(--lyx-cert-scale)) calc(24px * var(--lyx-cert-scale)) rgba(0, 0, 0, 0.22);
      background: radial-gradient(circle at 50% 34%, #181A21 0%, #0A0B0E 74%);
      color: #F6D37A;
      padding: calc(18px * var(--lyx-cert-scale)) calc(14px * var(--lyx-cert-scale));
    }
    .lyx-cert-seal-stars {
      color: #F6D37A;
      font-size: calc(11px * var(--lyx-cert-scale));
      letter-spacing: 0.22em;
      margin-bottom: calc(12px * var(--lyx-cert-scale));
    }
    .lyx-cert-seal-kicker,
    .lyx-cert-seal-footer {
      margin: 0;
      color: #F6D37A;
      font-size: calc(7.5px * var(--lyx-cert-scale));
      letter-spacing: 0.13em;
      text-transform: uppercase;
      font-weight: 700;
    }
    .lyx-cert-seal-title {
      margin: calc(6px * var(--lyx-cert-scale)) 0 calc(7px * var(--lyx-cert-scale));
      color: #FFFFFF;
      font-family: 'Space Grotesk', 'Inter', sans-serif;
      font-size: calc(18px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 1.04;
      letter-spacing: 0.02em;
      text-transform: uppercase;
    }
    .lyx-cert-seal-ribbon {
      width: calc(88px * var(--lyx-cert-scale));
      height: calc(45px * var(--lyx-cert-scale));
      margin: calc(-8px * var(--lyx-cert-scale)) auto 0;
      background: linear-gradient(180deg, #F2C760 0%, #C98D14 100%);
      color: #0B0B0D;
      font-size: calc(22px * var(--lyx-cert-scale));
      line-height: 1;
      padding: calc(20px * var(--lyx-cert-scale)) 0 calc(42px * var(--lyx-cert-scale));
      clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 83%, 0 100%);
      box-shadow: 0 calc(8px * var(--lyx-cert-scale)) calc(20px * var(--lyx-cert-scale)) rgba(0, 0, 0, 0.14);
    }
    .lyx-cert-hero-copy {
      position: relative;
      z-index: 1;
      max-width: calc(760px * var(--lyx-cert-scale));
    }
    .lyx-cert-title {
      margin: 0 0 calc(10px * var(--lyx-cert-scale));
      color: #FFFFFF;
      font-family: 'Space Grotesk', 'Inter', sans-serif;
      font-size: calc(54px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 0.96;
      letter-spacing: 0.015em;
      text-transform: uppercase;
      max-width: 11.2ch;
      text-wrap: balance;
    }
    .lyx-cert-subtitle {
      margin: 0 0 calc(18px * var(--lyx-cert-scale));
      color: #E6AC33;
      font-family: 'Space Grotesk', 'Inter', sans-serif;
      font-size: calc(15px * var(--lyx-cert-scale));
      letter-spacing: 0.14em;
      text-transform: uppercase;
      font-weight: 600;
    }
    .lyx-cert-lead {
      margin: 0;
      color: #EAE3D5;
      font-size: calc(13px * var(--lyx-cert-scale));
      line-height: 1.75;
      max-width: calc(610px * var(--lyx-cert-scale));
    }
    .lyx-cert-lead-accent { color: #E7B545; font-weight: 600; }
    .lyx-cert-body {
      padding: calc(20px * var(--lyx-cert-scale));
      background: #F4F0E8;
    }
    .lyx-cert-panel {
      background: #FFFFFF;
      border: 1px solid #E9E0CF;
      border-radius: calc(12px * var(--lyx-cert-scale));
      box-shadow: 0 calc(2px * var(--lyx-cert-scale)) calc(10px * var(--lyx-cert-scale)) rgba(11, 11, 13, 0.04);
      padding: calc(20px * var(--lyx-cert-scale)) calc(22px * var(--lyx-cert-scale));
    }
    .lyx-cert-panel + .lyx-cert-panel {
      margin-top: calc(16px * var(--lyx-cert-scale));
    }
    .lyx-cert-meta {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: calc(24px * var(--lyx-cert-scale));
      align-items: start;
    }
    .lyx-cert-meta-side + .lyx-cert-meta-side {
      border-left: 1px solid #ECE5D6;
      padding-left: calc(24px * var(--lyx-cert-scale));
    }
    .lyx-cert-persona {
      display: flex;
      align-items: flex-start;
      gap: calc(16px * var(--lyx-cert-scale));
    }
    .lyx-cert-avatar {
      width: calc(74px * var(--lyx-cert-scale));
      height: calc(74px * var(--lyx-cert-scale));
      border-radius: 999px;
      flex-shrink: 0;
      background: radial-gradient(circle at 35% 30%, #20242F 0%, #0E1015 74%);
      display: grid;
      place-items: center;
      box-shadow: inset 0 0 0 1px rgba(231, 181, 69, 0.06);
    }
    .lyx-cert-avatar svg {
      display: block;
      width: calc(38px * var(--lyx-cert-scale));
      height: calc(38px * var(--lyx-cert-scale));
    }
    .lyx-cert-rows {
      display: grid;
      gap: calc(16px * var(--lyx-cert-scale));
      margin: 0;
      min-width: 0;
    }
    .lyx-cert-row {
      display: grid;
      gap: calc(4px * var(--lyx-cert-scale));
      min-width: 0;
    }
    .lyx-cert-label {
      color: #5B5E66;
      font-size: calc(11px * var(--lyx-cert-scale));
      letter-spacing: 0.04em;
      text-transform: uppercase;
      font-weight: 700;
      line-height: 1.2;
    }
    .lyx-cert-value {
      color: #111318;
      font-size: calc(14px * var(--lyx-cert-scale));
      font-weight: 600;
      line-height: 1.35;
      overflow-wrap: anywhere;
    }
    .lyx-cert-detail-grid {
      display: grid;
      gap: calc(18px * var(--lyx-cert-scale));
    }
    .lyx-cert-detail {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      gap: calc(12px * var(--lyx-cert-scale));
      align-items: start;
    }
    .lyx-cert-detail-icon {
      display: grid;
      place-items: center;
      width: calc(34px * var(--lyx-cert-scale));
      height: calc(34px * var(--lyx-cert-scale));
      color: #E7B545;
      flex-shrink: 0;
    }
    .lyx-cert-detail-icon svg {
      display: block;
      width: calc(24px * var(--lyx-cert-scale));
      height: calc(24px * var(--lyx-cert-scale));
    }
    .lyx-cert-detail-copy {
      display: grid;
      gap: calc(4px * var(--lyx-cert-scale));
      min-width: 0;
    }
    .lyx-cert-signature {
      margin-top: calc(22px * var(--lyx-cert-scale));
      padding-top: calc(12px * var(--lyx-cert-scale));
      border-top: 1px solid #ECE5D6;
    }
    .lyx-cert-signature-mark {
      margin: 0 0 calc(8px * var(--lyx-cert-scale));
      color: #20242D;
      font-family: 'Brush Script MT', 'Snell Roundhand', cursive;
      font-size: calc(28px * var(--lyx-cert-scale));
      line-height: 1;
    }
    .lyx-cert-signature-line {
      width: min(100%, calc(220px * var(--lyx-cert-scale)));
      border-top: 1px solid #D7D0C0;
      margin-bottom: calc(10px * var(--lyx-cert-scale));
    }
    .lyx-cert-signature-name,
    .lyx-cert-signature-role {
      margin: 0;
      color: #2B2E36;
      font-size: calc(13px * var(--lyx-cert-scale));
      line-height: 1.5;
    }
    .lyx-cert-status {
      display: inline-flex;
      align-items: center;
      width: fit-content;
      border-radius: 999px;
      padding: calc(5px * var(--lyx-cert-scale)) calc(11px * var(--lyx-cert-scale));
      font-size: calc(11px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 1;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      background: #1DA34A;
      color: #FFFFFF;
      box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.12);
    }
    .lyx-cert-panel-title {
      margin: 0 0 calc(8px * var(--lyx-cert-scale));
      color: #111318;
      font-family: 'Space Grotesk', 'Inter', sans-serif;
      font-size: calc(17px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 1.15;
      text-transform: uppercase;
    }
    .lyx-cert-panel-copy {
      margin: 0 0 calc(18px * var(--lyx-cert-scale));
      color: #454952;
      font-size: calc(12.5px * var(--lyx-cert-scale));
      line-height: 1.65;
    }
    .lyx-cert-panel-copy .lyx-cert-lead-accent {
      color: #D29A24;
      font-weight: 700;
    }
    .lyx-cert-rights {
      list-style: none;
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: calc(14px * var(--lyx-cert-scale));
      padding: 0;
      margin: 0;
    }
    .lyx-cert-right {
      text-align: center;
      min-width: 0;
      max-width: calc(180px * var(--lyx-cert-scale));
      margin: 0 auto;
    }
    .lyx-cert-right-badge {
      width: calc(52px * var(--lyx-cert-scale));
      height: calc(52px * var(--lyx-cert-scale));
      border-radius: 999px;
      margin: 0 auto calc(10px * var(--lyx-cert-scale));
      background: radial-gradient(circle at 35% 30%, #1A1E27 0%, #090A0D 78%);
      display: grid;
      place-items: center;
      box-shadow: inset 0 0 0 1px rgba(231, 181, 69, 0.08);
    }
    .lyx-cert-right-badge svg {
      display: block;
      width: calc(28px * var(--lyx-cert-scale));
      height: calc(28px * var(--lyx-cert-scale));
    }
    .lyx-cert-right-title {
      margin: 0 0 calc(6px * var(--lyx-cert-scale));
      color: #101114;
      font-size: calc(12.5px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 1.35;
      text-transform: capitalize;
    }
    .lyx-cert-right-body {
      margin: 0;
      color: #555961;
      font-size: calc(11.5px * var(--lyx-cert-scale));
      line-height: 1.6;
    }
    .lyx-cert-right-check {
      margin: calc(10px * var(--lyx-cert-scale)) 0 0;
      color: #1EAF57;
      font-size: calc(20px * var(--lyx-cert-scale));
      line-height: 1;
      font-weight: 700;
    }
    .lyx-cert-columns {
      display: grid;
      grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
      gap: calc(24px * var(--lyx-cert-scale));
    }
    .lyx-cert-column + .lyx-cert-column {
      border-left: 1px solid #ECE5D6;
      padding-left: calc(24px * var(--lyx-cert-scale));
    }
    .lyx-cert-notes {
      margin: 0;
      padding-left: calc(18px * var(--lyx-cert-scale));
      color: #2B2E36;
      font-size: calc(12px * var(--lyx-cert-scale));
      line-height: 1.75;
    }
    .lyx-cert-note + .lyx-cert-note {
      margin-top: calc(6px * var(--lyx-cert-scale));
    }
    .lyx-cert-license-copy {
      margin: 0 0 calc(12px * var(--lyx-cert-scale));
      color: #2B2E36;
      font-size: calc(12px * var(--lyx-cert-scale));
      line-height: 1.75;
    }
    .lyx-cert-mini-brand {
      display: flex;
      align-items: center;
      gap: calc(10px * var(--lyx-cert-scale));
      margin-top: calc(16px * var(--lyx-cert-scale));
    }
    .lyx-cert-mini-brand svg {
      display: block;
      width: calc(26px * var(--lyx-cert-scale));
      height: calc(26px * var(--lyx-cert-scale));
      flex-shrink: 0;
    }
    .lyx-cert-mini-wordmark {
      margin: 0;
      color: #14161B;
      font-family: 'Space Grotesk', 'Inter', sans-serif;
      font-size: calc(18px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 1;
      text-transform: uppercase;
    }
    .lyx-cert-mini-subtitle {
      margin: calc(3px * var(--lyx-cert-scale)) 0 0;
      color: #666A72;
      font-size: calc(10px * var(--lyx-cert-scale));
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
    .lyx-cert-footer {
      display: grid;
      grid-template-columns: auto auto minmax(0, 1fr);
      align-items: center;
      gap: calc(22px * var(--lyx-cert-scale));
      padding: calc(20px * var(--lyx-cert-scale)) calc(24px * var(--lyx-cert-scale));
      background: #0C0D10;
      color: #F6F1E7;
    }
    .lyx-cert-verify {
      display: flex;
      align-items: center;
      gap: calc(16px * var(--lyx-cert-scale));
      min-width: 0;
    }
    .lyx-cert-qr {
      display: block;
      width: calc(92px * var(--lyx-cert-scale));
      height: calc(92px * var(--lyx-cert-scale));
      border-radius: calc(6px * var(--lyx-cert-scale));
      background: #FFFFFF;
      flex-shrink: 0;
    }
    .lyx-cert-verify-kicker {
      margin: 0 0 calc(4px * var(--lyx-cert-scale));
      color: #E7B545;
      font-size: calc(12px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 1.3;
      text-transform: uppercase;
    }
    .lyx-cert-verify-copy,
    .lyx-cert-footer-text {
      margin: 0;
      color: #E6DEC9;
      font-size: calc(12px * var(--lyx-cert-scale));
      line-height: 1.65;
    }
    .lyx-cert-verify-link {
      margin: calc(6px * var(--lyx-cert-scale)) 0 0;
      color: #E7B545;
      font-size: calc(12px * var(--lyx-cert-scale));
      font-weight: 700;
      line-height: 1.35;
      overflow-wrap: anywhere;
    }
    .lyx-cert-footer-badge {
      display: grid;
      place-items: center;
      width: calc(58px * var(--lyx-cert-scale));
      height: calc(58px * var(--lyx-cert-scale));
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.02);
      box-shadow: inset 0 0 0 1px rgba(231, 181, 69, 0.08);
      flex-shrink: 0;
      align-self: center;
    }
    .lyx-cert-footer-badge svg {
      display: block;
      width: calc(36px * var(--lyx-cert-scale));
      height: calc(36px * var(--lyx-cert-scale));
    }
    .lyx-cert-footer-note {
      display: grid;
      gap: calc(7px * var(--lyx-cert-scale));
      min-width: 0;
      align-content: center;
    }
    .lyx-cert-footer-text strong {
      color: #F5F1E8;
      font-weight: 700;
    }
    @media (max-width: 1100px) {
      .lyx-cert-rights {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
      .lyx-cert-waveform {
        right: calc(180px * var(--lyx-cert-scale));
        width: calc(300px * var(--lyx-cert-scale));
      }
    }
    @media (max-width: 860px) {
      .lyx-cert-hero-row,
      .lyx-cert-meta,
      .lyx-cert-columns,
      .lyx-cert-footer {
        grid-template-columns: 1fr;
        display: grid;
      }
      .lyx-cert-hero-row {
        gap: calc(16px * var(--lyx-cert-scale));
      }
      .lyx-cert-waveform {
        right: calc(18px * var(--lyx-cert-scale));
        top: calc(82px * var(--lyx-cert-scale));
        width: calc(210px * var(--lyx-cert-scale));
      }
      .lyx-cert-seal {
        width: min(calc(174px * var(--lyx-cert-scale)), 48vw);
      }
      .lyx-cert-meta-side + .lyx-cert-meta-side,
      .lyx-cert-column + .lyx-cert-column {
        border-left: 0;
        padding-left: 0;
        border-top: 1px solid #ECE5D6;
        padding-top: calc(20px * var(--lyx-cert-scale));
      }
      .lyx-cert-rights {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .lyx-cert-footer-badge {
        order: 3;
      }
    }
    @media (max-width: 560px) {
      .lyx-cert-hero,
      .lyx-cert-body,
      .lyx-cert-footer {
        padding-left: calc(16px * var(--lyx-cert-scale));
        padding-right: calc(16px * var(--lyx-cert-scale));
      }
      .lyx-cert-title {
        font-size: calc(34px * var(--lyx-cert-scale));
        max-width: 8ch;
      }
      .lyx-cert-rights {
        grid-template-columns: minmax(0, 1fr);
      }
      .lyx-cert-persona,
      .lyx-cert-verify {
        align-items: flex-start;
      }
    }
  `;
}

export function buildCommercialCertificateBody(
  fields: CommercialCertificateFields,
  options: { generatedAt?: string; scale?: number } = {}
): string {
  const generatedAt = escapeHtml(options.generatedAt ?? (fields.purchaseDate || '—'));
  const scaleStyle =
    typeof options.scale === 'number'
      ? ` style="--lyx-cert-scale: ${escapeHtml(String(options.scale))}"`
      : '';
  const rows = commercialCertificateRows(fields);
  const leftRows = rows
    .slice(0, 4)
    .map(
      (row) => `
        <div class="lyx-cert-row">
          <span class="lyx-cert-label">${escapeHtml(row.label)}</span>
          <span class="lyx-cert-value">${escapeHtml(row.value)}</span>
        </div>
      `
    )
    .join('');
  const details = [
    detailBlock(rows[4].label, rows[4].value, 'calendar'),
    detailBlock(rows[5].label, rows[5].value, 'calendar'),
    detailBlock('License status', commercialCertificateStatusLabel(fields.status), 'user'),
  ].join('');
  const rights = commercialCertificateTiles()
    .map(
      (right) => `
        <li class="lyx-cert-right">
          <div class="lyx-cert-right-badge" aria-hidden="true">${rightsIconSvg(right.title)}</div>
          <p class="lyx-cert-right-title">${escapeHtml(right.title)}</p>
          <p class="lyx-cert-right-body">${escapeHtml(right.body)}</p>
          <p class="lyx-cert-right-check" aria-hidden="true">✓</p>
        </li>
      `
    )
    .join('');
  const notes = certificateNotes()
    .map((note) => `<li class="lyx-cert-note">${escapeHtml(note)}</li>`)
    .join('');
  const verifyUrl = escapeHtml(verifyUrlText(fields));

  return `
    <section class="lyx-cert-root" aria-label="Commercial license certificate"${scaleStyle}>
      <div class="lyx-cert-hero">
        <div class="lyx-cert-hero-row">
          <div class="lyx-cert-brand">
            <div class="lyx-cert-brand-mark">${brandMarkSvg(28, '#D6A329', '#0B0B0D')}</div>
            <div class="lyx-cert-wordmark-block">
              <p class="lyx-cert-wordmark">Lyrixsa <span class="lyx-cert-wordmark-accent">AI</span></p>
              <p class="lyx-cert-brand-subtitle">AI Music Studio</p>
            </div>
          </div>
          <div class="lyx-cert-seal" aria-hidden="true">
            <div class="lyx-cert-seal-disc">
              <div class="lyx-cert-seal-stars">★ ★ ★ ★ ★</div>
              <p class="lyx-cert-seal-kicker">Commercial</p>
              <p class="lyx-cert-seal-title">License</p>
              <p class="lyx-cert-seal-footer">Unlocked</p>
            </div>
            <div class="lyx-cert-seal-ribbon"></div>
          </div>
        </div>
        <div class="lyx-cert-waveform" aria-hidden="true"></div>
        <div class="lyx-cert-hero-copy">
          <h1 class="lyx-cert-title">Commercial License</h1>
          <p class="lyx-cert-subtitle">Certificate of Rights</p>
          <p class="lyx-cert-lead">
            This document certifies that you have the full commercial rights to use, sell,
            distribute, and profit from music created with
            <span class="lyx-cert-lead-accent"> Lyrixsa AI</span>.
          </p>
        </div>
      </div>

      <div class="lyx-cert-body">
        <div class="lyx-cert-panel lyx-cert-meta">
          <div class="lyx-cert-meta-side">
            <div class="lyx-cert-persona">
              <div class="lyx-cert-avatar">${outlinedIconSvg('user', 38)}</div>
              <div class="lyx-cert-rows">${leftRows}</div>
            </div>
          </div>
          <div class="lyx-cert-meta-side">
            <div class="lyx-cert-detail-grid">${details}</div>
            <div class="lyx-cert-signature">
              <p class="lyx-cert-signature-mark">Lyrixsa Team</p>
              <div class="lyx-cert-signature-line"></div>
              <p class="lyx-cert-signature-name">Lyrixsa AI Team</p>
              <p class="lyx-cert-signature-role">Authorized signatory</p>
            </div>
          </div>
        </div>

        <div class="lyx-cert-panel">
          <h2 class="lyx-cert-panel-title">Your commercial rights</h2>
          <p class="lyx-cert-panel-copy">
            With this license, you are granted the following rights for music created using
            <span class="lyx-cert-lead-accent"> Lyrixsa AI</span>:
          </p>
          <ul class="lyx-cert-rights">${rights}</ul>
        </div>

        <div class="lyx-cert-panel lyx-cert-columns">
          <div class="lyx-cert-column">
            <h2 class="lyx-cert-panel-title">Important notes</h2>
            <ul class="lyx-cert-notes">${notes}</ul>
          </div>
          <div class="lyx-cert-column">
            <h2 class="lyx-cert-panel-title">License certificate</h2>
            <p class="lyx-cert-license-copy">${escapeHtml(CERTIFICATE_NOTICE)}</p>
            <p class="lyx-cert-license-copy">
              Certificate #${Number(fields.downloadCount) + 1} · Version ${escapeHtml(fields.version)}<br/>
              Generated ${generatedAt}
            </p>
            <div class="lyx-cert-mini-brand">
              ${brandMarkSvg(26, '#D6A329', '#0B0B0D')}
              <div>
                <p class="lyx-cert-mini-wordmark">Lyrixsa AI</p>
                <p class="lyx-cert-mini-subtitle">AI Music Studio</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="lyx-cert-footer">
        <div class="lyx-cert-verify">
          <img class="lyx-cert-qr" src="${escapeHtml(fields.qrSrc)}" alt="Verification QR code" />
          <div>
            <p class="lyx-cert-verify-kicker">Verify this license</p>
            <p class="lyx-cert-verify-copy">Scan the QR code or visit the link below to verify authenticity.</p>
            <p class="lyx-cert-verify-link">${verifyUrl}</p>
          </div>
        </div>
        <div class="lyx-cert-footer-badge">${outlinedIconSvg('shield', 34)}</div>
        <div class="lyx-cert-footer-note">
          <p class="lyx-cert-footer-text"><strong>Official Lyrixsa AI commercial license document.</strong></p>
          <p class="lyx-cert-footer-text">Keep this certificate for your records and use the public verification page when sharing proof with a third party.</p>
        </div>
      </div>
    </section>
  `;
}
